import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

// ============================================================
// CONFIGURATION
// ============================================================

const API = '/api/products';

const ORGANIZATIONS = ['The Bengal Craft', 'COG'];

const emptyForm = () => ({
  sku: '',
  name: '',
  category: '',
  price: '',
  organization: 'The Bengal Craft',
});

const money = (value) =>
  Number(value || 0).toLocaleString('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2,
  });

const errorMessage = (error) =>
  error?.response?.data?.error ||
  error?.message ||
  'Something went wrong.';

// ============================================================
// COMPONENT
// ============================================================

export default function ProductMaster() {
  const [products, setProducts] = useState([]);

  // Filters
  const [search, setSearch] = useState('');
  const [organizationFilter, setOrganizationFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('true');

  // Product form
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);

  // UI state
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // ============================================================
  // LOAD PRODUCTS
  // ============================================================

  const loadProducts = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const response = await axios.get(API, {
        params: { active: 'all' },
      });

      if (!Array.isArray(response.data)) {
        throw new Error('Unexpected products response.');
      }

      setProducts(response.data);
    } catch (err) {
      setError(`Unable to load products: ${errorMessage(err)}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  // ============================================================
  // FILTER OPTIONS
  // ============================================================

  const categories = useMemo(() => {
    return [
      ...new Set(
        products
          .map((product) => product.category)
          .filter(Boolean)
      ),
    ].sort((a, b) => a.localeCompare(b));
  }, [products]);

  const filteredProducts = useMemo(() => {
    const query = search.trim().toLocaleLowerCase();

    return products.filter((product) => {
      if (
        organizationFilter !== 'all' &&
        product.organization !== organizationFilter
      ) {
        return false;
      }

      if (
        categoryFilter !== 'all' &&
        product.category !== categoryFilter
      ) {
        return false;
      }

      if (
        statusFilter !== 'all' &&
        String(product.isActive) !== statusFilter
      ) {
        return false;
      }

      if (!query) return true;

      return [
        product.sku,
        product.name,
        product.category,
        product.organization,
      ].some((value) =>
        String(value || '').toLocaleLowerCase().includes(query)
      );
    });
  }, [
    products,
    search,
    organizationFilter,
    categoryFilter,
    statusFilter,
  ]);

  // ============================================================
  // FORM ACTIONS
  // ============================================================

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setError('');
    setSuccess('');
    setShowForm(true);
  };

  const openEdit = (product) => {
    setEditingId(product._id);

    setForm({
      sku: product.sku || '',
      name: product.name || '',
      category: product.category || '',
      price: String(product.price ?? ''),
      organization: product.organization || 'The Bengal Craft',
    });

    setError('');
    setSuccess('');
    setShowForm(true);
  };

  const closeForm = () => {
    if (saving) return;

    setShowForm(false);
    setEditingId(null);
    setForm(emptyForm());
  };

  const updateField = (field, value) => {
    setForm((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  // ============================================================
  // CREATE / UPDATE PRODUCT
  // ============================================================

  const saveProduct = async (event) => {
    event.preventDefault();

    const payload = {
      sku: form.sku.trim().toUpperCase(),
      name: form.name.trim(),
      category: form.category.trim(),
      price: Number(form.price),
      organization: form.organization,
    };

    if (!payload.sku || !payload.name || !payload.category) {
      setError('SKU, product name and category are required.');
      return;
    }

    if (
      form.price === '' ||
      !Number.isFinite(payload.price) ||
      payload.price < 0
    ) {
      setError('Enter a valid price of zero or more.');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      if (editingId) {
        await axios.put(`${API}/${editingId}`, payload);
      } else {
        await axios.post(API, payload);
      }

      const wasEditing = Boolean(editingId);

      setShowForm(false);
      setEditingId(null);
      setForm(emptyForm());

      await loadProducts();

      setSuccess(
        wasEditing
          ? 'Product updated successfully.'
          : 'Product created successfully.'
      );
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  // ============================================================
  // ARCHIVE / REACTIVATE PRODUCT
  // ============================================================

  const changeStatus = async (product) => {
    const nextActive = !product.isActive;

    const action = nextActive ? 'Reactivate' : 'Archive';

    const confirmed = window.confirm(
      `${action} ${product.sku} — ${product.name}?`
    );

    if (!confirmed) return;

    setBusyId(product._id);
    setError('');
    setSuccess('');

    try {
      await axios.patch(
        `${API}/${product._id}/status`,
        { isActive: nextActive }
      );

      setProducts((previous) =>
        previous.map((entry) =>
          entry._id === product._id
            ? { ...entry, isActive: nextActive }
            : entry
        )
      );

      setSuccess(
        nextActive
          ? 'Product reactivated.'
          : 'Product archived. Historical invoice references remain intact.'
      );
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">

      {/* PAGE HEADER */}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            Product Master
          </h1>

          <p className="text-sm text-gray-500 mt-1">
            Manage standardized products used for invoicing
            and historical order mapping.
          </p>
        </div>

        <button
          type="button"
          onClick={openCreate}
          className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-medium"
        >
          + Add Product
        </button>
      </div>

      {/* MESSAGES */}

      {error && (
        <div
          role="alert"
          className="mb-4 rounded-lg border border-red-200 bg-red-50 text-red-700 p-3"
        >
          {error}
        </div>
      )}

      {success && (
        <div
          role="status"
          className="mb-4 rounded-lg border border-green-200 bg-green-50 text-green-700 p-3"
        >
          {success}
        </div>
      )}

      {/* FILTERS */}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">

          {/* SEARCH */}

          <div>
            <label
              htmlFor="product-search"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Search
            </label>

            <input
              id="product-search"
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="SKU, name or category"
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            />
          </div>

          {/* ORGANIZATION FILTER */}

          <div>
            <label
              htmlFor="product-organization"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Organization
            </label>

            <select
              id="product-organization"
              value={organizationFilter}
              onChange={(event) =>
                setOrganizationFilter(event.target.value)
              }
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            >
              <option value="all">All organizations</option>

              {ORGANIZATIONS.map((org) => (
                <option key={org} value={org}>
                  {org}
                </option>
              ))}
            </select>
          </div>

          {/* CATEGORY FILTER */}

          <div>
            <label
              htmlFor="product-category"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Category
            </label>

            <select
              id="product-category"
              value={categoryFilter}
              onChange={(event) =>
                setCategoryFilter(event.target.value)
              }
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            >
              <option value="all">All categories</option>

              {categories.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>

          {/* STATUS FILTER */}

          <div>
            <label
              htmlFor="product-status"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Status
            </label>

            <select
              id="product-status"
              value={statusFilter}
              onChange={(event) =>
                setStatusFilter(event.target.value)
              }
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            >
              <option value="true">Active</option>
              <option value="false">Archived</option>
              <option value="all">All statuses</option>
            </select>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-sm text-gray-600">
          <span>
            Showing {filteredProducts.length} of {products.length} products
          </span>

          <button
            type="button"
            onClick={loadProducts}
            disabled={loading}
            className="text-indigo-600 hover:text-indigo-800 disabled:opacity-50"
          >
            {loading ? 'Refreshing...' : 'Refresh list'}
          </button>
        </div>
      </div>

      {/* ADD / EDIT PRODUCT FORM */}

      {showForm && (
        <div className="bg-white rounded-xl shadow-sm border border-indigo-200 p-5 mb-5">

          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">
              {editingId ? 'Edit Product' : 'Add Product'}
            </h2>

            <button
              type="button"
              onClick={closeForm}
              disabled={saving}
              className="text-gray-600 hover:text-gray-900"
            >
              Close ✕
            </button>
          </div>

          <form onSubmit={saveProduct}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

              {/* SKU */}

              <div>
                <label
                  htmlFor="master-sku"
                  className="block text-sm font-medium mb-1"
                >
                  SKU *
                </label>

                <input
                  id="master-sku"
                  required
                  maxLength={100}
                  value={form.sku}
                  onChange={(event) =>
                    updateField(
                      'sku',
                      event.target.value.toUpperCase()
                    )
                  }
                  placeholder="GH-ABT-001"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>

              {/* PRODUCT NAME */}

              <div>
                <label
                  htmlFor="master-name"
                  className="block text-sm font-medium mb-1"
                >
                  Standardized Product Name *
                </label>

                <input
                  id="master-name"
                  required
                  value={form.name}
                  onChange={(event) =>
                    updateField('name', event.target.value)
                  }
                  placeholder="Tyas Goru — ট্যাঁশ গরু"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>

              {/* CATEGORY */}

              <div>
                <label
                  htmlFor="master-category"
                  className="block text-sm font-medium mb-1"
                >
                  Category *
                </label>

                <input
                  id="master-category"
                  required
                  list="product-category-suggestions"
                  value={form.category}
                  onChange={(event) =>
                    updateField('category', event.target.value)
                  }
                  placeholder="Abol Tabol"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />

                <datalist id="product-category-suggestions">
                  {categories.map((category) => (
                    <option
                      key={category}
                      value={category}
                    />
                  ))}
                </datalist>
              </div>

              {/* PRICE */}

              <div>
                <label
                  htmlFor="master-price"
                  className="block text-sm font-medium mb-1"
                >
                  Default Selling Price (INR) *
                </label>

                <input
                  id="master-price"
                  required
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.price}
                  onChange={(event) =>
                    updateField('price', event.target.value)
                  }
                  placeholder="800"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>

              {/* ORGANIZATION */}

              <div>
                <label
                  htmlFor="master-org"
                  className="block text-sm font-medium mb-1"
                >
                  Organization *
                </label>

                <select
                  id="master-org"
                  disabled={!!editingId}
                  value={form.organization}
                  onChange={(event) =>
                    updateField(
                      'organization',
                      event.target.value
                    )
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  {ORGANIZATIONS.map((org) => (
                    <option key={org} value={org}>
                      {org}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* FORM BUTTONS */}

            <div className="mt-5 flex flex-wrap gap-2">
              <button
                type="submit"
                disabled={saving}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg disabled:opacity-50"
              >
                {saving
                  ? 'Saving...'
                  : editingId
                    ? 'Save Changes'
                    : 'Create Product'}
              </button>

              <button
                type="button"
                onClick={closeForm}
                disabled={saving}
                className="border border-gray-300 px-4 py-2 rounded-lg"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* PRODUCT TABLE */}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">

            <thead className="bg-gray-50 text-gray-700 border-b">
              <tr>
                <th className="px-4 py-3 font-semibold">
                  SKU
                </th>

                <th className="px-4 py-3 font-semibold">
                  Product Name
                </th>

                <th className="px-4 py-3 font-semibold">
                  Category
                </th>

                <th className="px-4 py-3 font-semibold">
                  Organization
                </th>

                <th className="px-4 py-3 font-semibold text-right">
                  Price
                </th>

                <th className="px-4 py-3 font-semibold">
                  Status
                </th>

                <th className="px-4 py-3 font-semibold text-right">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100">
              {filteredProducts.map((product) => (
                <tr
                  key={product._id}
                  className="hover:bg-gray-50"
                >
                  <td className="px-4 py-3 font-mono whitespace-nowrap">
                    {product.sku}
                  </td>

                  <td className="px-4 py-3 font-medium">
                    {product.name}
                  </td>

                  <td className="px-4 py-3">
                    {product.category}
                  </td>

                  <td className="px-4 py-3 whitespace-nowrap">
                    {product.organization}
                  </td>

                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {money(product.price)}
                  </td>

                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${
                        product.isActive
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {product.isActive
                        ? 'Active'
                        : 'Archived'}
                    </span>
                  </td>

                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button
                      type="button"
                      onClick={() => openEdit(product)}
                      className="text-indigo-600 hover:underline mr-3"
                    >
                      Edit
                    </button>

                    <button
                      type="button"
                      disabled={busyId === product._id}
                      onClick={() => changeStatus(product)}
                      className={`${
                        product.isActive
                          ? 'text-amber-700'
                          : 'text-green-700'
                      } hover:underline disabled:opacity-50`}
                    >
                      {busyId === product._id
                        ? 'Working...'
                        : product.isActive
                          ? 'Archive'
                          : 'Reactivate'}
                    </button>
                  </td>
                </tr>
              ))}

              {!loading && filteredProducts.length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    className="px-4 py-12 text-center text-gray-500"
                  >
                    No products match your filters.
                    Use "Add Product" to create your first
                    standardized product.
                  </td>
                </tr>
              )}

              {loading && (
                <tr>
                  <td
                    colSpan={7}
                    className="px-4 py-12 text-center text-gray-500"
                  >
                    Loading products...
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}